import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CreditCard, Copy, ExternalLink, Banknote } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { money } from "@/lib/orderStatus";

const isPayLink = (v) => /^https?:\/\//i.test((v || "").trim());

// Reusable online payment UI (Mobile Money methods + Stripe card) for an order in "a_payer" state.
// onPaid() is called after a Mobile Money payment is confirmed by the customer.
export default function PaymentPanel({ order, onPaid }) {
  const { t } = useTranslation();
  const currency = order.currency || "XOF";
  const [method, setMethod] = useState(null);
  const [showRef, setShowRef] = useState(false);
  const [reference, setReference] = useState("");
  const [sendInvoice, setSendInvoice] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [paying, setPaying] = useState(false);
  const [interacInfo, setInteracInfo] = useState(null);

  const confirmPaid = async () => {
    if (!reference.trim()) { toast.error(t("tracking.refRequired")); return; }
    setConfirming(true);
    try {
      await api.post(`/public/orders/${order.order_id}/pay`, { method, reference, send_invoice_whatsapp: sendInvoice });
      setShowRef(false);
      toast.success(t("tracking.paymentConfirmed"));
      onPaid?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  const payByCard = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/orders/${order.order_id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const chooseInterac = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/orders/${order.order_id}/choose-interac`);
      setInteracInfo({ handle: data.business_interac || "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="pay-section">
      {(order.payment_methods?.length > 0) ? (
        <>
          <p className="text-sm font-semibold text-[#5C574F] text-center">{t("tracking.choosePayment")}</p>
          <div className={`grid gap-2 ${order.payment_methods.length === 1 ? "grid-cols-1" : order.payment_methods.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
            {order.payment_methods.map((m) => (
              <button
                key={m.id}
                data-testid={`select-method-${m.id}`}
                onClick={() => setMethod(m.id)}
                className={`h-12 rounded-xl font-bold text-sm text-white transition-all ${method === m.id ? "ring-2 ring-offset-2 ring-[#1B4332] scale-[1.02]" : "opacity-90"}`}
                style={{ background: m.color || "#E85D04" }}
              >
                {m.label}
              </button>
            ))}
          </div>
          {method && (() => {
            const m = order.payment_methods.find((x) => x.id === method);
            if (!m) return null;
            const num = (m.number || "").trim();
            const isLink = isPayLink(num);
            const showNumber = num && !isLink;
            return (
              <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 text-center space-y-3" data-testid="qr-display">
                <p className="text-sm font-semibold text-[#5C574F]">{t("tracking.payVia", { amount: money(order.total, currency), method: m.label })}</p>

                {m.holder && (
                  <p className="text-sm text-[#5C574F]" data-testid="account-holder">
                    {t("tracking.accountName")} <span className="font-display font-extrabold text-[#1A1A1A]">{m.holder}</span>
                  </p>
                )}

                {isLink && (
                  <a
                    href={num}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="deep-pay-btn"
                    className="w-full h-14 rounded-2xl text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                    style={{ background: m.color || "#E85D04" }}
                  >
                    <ExternalLink className="h-5 w-5" /> {t("tracking.payDirectly", { method: m.label })}
                  </a>
                )}

                {m.qr_url && (
                  <div className="pt-1">
                    <p className="text-xs text-[#9c968a] mb-2">{t("tracking.scanQr")}</p>
                    <img src={fileUrl(m.qr_url)} alt={`QR ${m.label}`} className="h-40 w-40 object-contain mx-auto rounded-xl border border-[#F0ece4]" data-testid="qr-image" />
                  </div>
                )}

                {showNumber && (
                  <div className="pt-1">
                    <p className="text-sm text-[#5C574F]">{t("tracking.numberToUse")}</p>
                    <p className="font-display text-lg font-extrabold text-[#1A1A1A]" data-testid="merchant-number">{num}</p>
                    <button
                      data-testid="copy-number-btn"
                      onClick={() => { navigator.clipboard?.writeText(num); toast.success(t("tracking.numberCopied")); }}
                      className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#F9F6F0] text-[#5C574F] text-sm font-semibold hover:text-[#E85D04] transition-colors"
                    >
                      <Copy className="h-4 w-4" /> {t("tracking.copyNumber")}
                    </button>
                  </div>
                )}

                {!num && !m.qr_url && (
                  <p className="text-xs text-[#9c968a]">{t("tracking.noMethodInfo")}</p>
                )}

                <p className="text-xs text-[#9c968a] pt-1">{t("tracking.afterPay")}</p>
              </div>
            );
          })()}
          <button
            data-testid="paid-btn"
            disabled={!method}
            onClick={() => setShowRef(true)}
            className="w-full h-14 rounded-2xl bg-[#2D6A4F] text-white font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("tracking.paidBtn", { amount: money(order.total, currency) })}
          </button>
          {!method && <p className="text-xs text-center text-[#9c968a]">{t("tracking.selectMethodFirst")}</p>}
        </>
      ) : order.country === "CA" ? (
        interacInfo ? (
          <div className="rounded-xl border-2 border-[#E85D04]/40 bg-[#E85D04]/5 p-4 space-y-2" data-testid="interac-instructions">
            <p className="text-sm font-bold flex items-center gap-2 text-[#E85D04]"><Banknote className="h-4 w-4" /> {t("tracking.interacTitle")}</p>
            <div className="bg-white rounded-lg p-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[#5C574F]">{t("tracking.interacAmount")}</span><span className="font-bold text-[#1A1A1A]">{money(order.total, currency)}</span></div>
              <div className="flex justify-between gap-2 items-center">
                <span className="text-[#5C574F]">{t("tracking.interacSendTo")}</span>
                <span className="font-semibold text-[#1A1A1A] break-all text-right flex items-center gap-1.5" data-testid="interac-handle">
                  {interacInfo.handle || "—"}
                  {interacInfo.handle && (
                    <button data-testid="copy-interac-btn" onClick={() => { navigator.clipboard?.writeText(interacInfo.handle); toast.success(t("tracking.numberCopied")); }} className="text-[#E85D04] hover:opacity-70">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              </div>
              <div className="flex justify-between"><span className="text-[#5C574F]">{t("tracking.interacRef")}</span><span className="font-mono font-semibold text-[#1A1A1A]">#{order.order_id.slice(-6)}</span></div>
            </div>
            <p className="text-xs text-[#9c968a]">{t("tracking.interacNote")}</p>
          </div>
        ) : (
        <>
          <p className="text-sm font-semibold text-[#5C574F] text-center">{order.interac_enabled ? t("tracking.payChooseMethod") : t("tracking.payByCardTitle")}</p>
          <button data-testid="pay-card-btn" onClick={payByCard} disabled={paying} className="w-full h-14 rounded-2xl bg-[#635BFF] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
            <CreditCard className="h-5 w-5" /> {paying ? t("tracking.redirecting") : t("tracking.payByCardBtn", { amount: money(order.total, currency) })}
          </button>
          {order.interac_enabled && (
            <button data-testid="pay-interac-btn" onClick={chooseInterac} disabled={paying} className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
              <Banknote className="h-5 w-5" /> {t("tracking.payByInteracBtn")}
            </button>
          )}
          <p className="text-xs text-center text-[#9c968a]">{t("tracking.stripeSecure")}</p>
        </>
        )
      ) : (
        <p className="text-center text-sm text-[#5C574F] bg-[#FFBA08]/15 rounded-xl p-4" data-testid="no-pay-info">
          {t("tracking.noPayInfo")}
        </p>
      )}

      {showRef && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowRef(false)} data-testid="ref-modal">
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6">
            <h3 className="font-display text-xl font-extrabold mb-1">{t("tracking.confirmPaymentTitle")}</h3>
            <p className="text-sm text-[#5C574F] mb-4">{t("tracking.refDesc")}</p>
            <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">{t("tracking.refLabel")}</label>
            <input
              data-testid="ref-input"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t("tracking.refPh")}
              className="w-full h-14 bg-[#F9F6F0] rounded-xl px-4 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20 mb-4"
            />
            <label className="flex items-center gap-3 mb-5 cursor-pointer" data-testid="invoice-whatsapp-toggle">
              <input type="checkbox" checked={sendInvoice} onChange={(e) => setSendInvoice(e.target.checked)} className="h-5 w-5 accent-[#25D366]" />
              <span className="text-sm text-[#5C574F]">{t("tracking.receiveInvoice")}</span>
            </label>
            <button
              data-testid="confirm-ref-btn"
              onClick={confirmPaid}
              disabled={confirming}
              className="w-full h-14 rounded-2xl bg-[#2D6A4F] text-white font-bold active:scale-95 transition-transform disabled:opacity-60"
            >
              {confirming ? t("tracking.confirming") : t("tracking.confirmMyPayment")}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export { isPayLink };
